import { computed, reactive, ref, type ComputedRef } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { createPrinter, deletePrinter, fetchPrinters, updatePrinter, type AdminPrinter, type PrinterSavePayload } from '@/api/printer'

export function usePrinterCenter(options: {
  isPrinterCenter: ComputedRef<boolean>
  ensureEditorOptions: () => Promise<void>
}) {
  const loadingPrinters = ref(false)
  const printerRows = ref<AdminPrinter[]>([])
  const printerEditorVisible = ref(false)
  const printerSubmitting = ref(false)
  const printerStoreId = ref('')
  const printerForm = reactive<PrinterSavePayload>({
    id: undefined,
    name: '',
    storeIds: '',
    bindCode: '',
    status: 'ONLINE',
  })

  const isEditingPrinter = computed(() => Boolean(printerForm.id))
  const printerStats = computed(() => {
    return [
      { label: '设备总数', value: String(printerRows.value.length), sub: '实时查询', tone: 'brand' as const },
      { label: '在线设备', value: String(printerRows.value.filter((item) => item.status === 'ONLINE').length), sub: '可正常打印', tone: 'success' as const },
      { label: '异常设备', value: String(printerRows.value.filter((item) => ['OFFLINE', 'PAPER_OUT'].includes(item.status || '')).length), sub: '需要处理', tone: 'warning' as const },
      { label: '绑定门店', value: String(new Set(printerRows.value.flatMap((item) => parseStoreIds(item.storeIds))).size), sub: '覆盖门店数', tone: 'brand' as const },
    ]
  })

  async function loadPrinters(): Promise<void> {
    if (!options.isPrinterCenter.value) return
    loadingPrinters.value = true
    try {
      printerRows.value = await fetchPrinters()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '打印设备加载失败')
    } finally {
      loadingPrinters.value = false
    }
  }

  async function openPrinterEditor(item?: AdminPrinter): Promise<void> {
    await options.ensureEditorOptions()
    Object.assign(printerForm, {
      id: item?.id,
      name: item?.name || '',
      storeIds: item?.storeIds || '',
      bindCode: '',
      status: item?.status || 'ONLINE',
    })
    printerStoreId.value = parseStoreIds(item?.storeIds || '')[0] || ''
    printerEditorVisible.value = true
  }

  async function submitPrinter(): Promise<void> {
    const name = printerForm.name.trim()
    if (!name) {
      ElMessage.warning('请填写设备名称')
      return
    }
    printerSubmitting.value = true
    try {
      const payload: PrinterSavePayload = {
        id: printerForm.id,
        name,
        storeIds: printerStoreId.value ? JSON.stringify([printerStoreId.value]) : undefined,
        bindCode: printerForm.bindCode?.trim() || undefined,
        status: printerForm.status?.trim() || undefined,
      }
      if (isEditingPrinter.value) {
        await updatePrinter(payload)
        ElMessage.success('打印设备已更新')
      } else {
        delete payload.id
        await createPrinter(payload)
        ElMessage.success('打印设备已创建')
      }
      printerEditorVisible.value = false
      await loadPrinters()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '打印设备保存失败')
    } finally {
      printerSubmitting.value = false
    }
  }

  async function removePrinter(item: AdminPrinter): Promise<void> {
    try {
      await ElMessageBox.confirm(`确定删除打印设备“${item.name}”吗？删除后不可恢复。`, '删除打印设备', {
        type: 'warning',
      })
    } catch {
      return
    }
    try {
      await deletePrinter(item.id)
      ElMessage.success('打印设备已删除')
      await loadPrinters()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '打印设备删除失败')
    }
  }

  return {
    loadingPrinters,
    printerRows,
    printerEditorVisible,
    printerSubmitting,
    printerForm,
    printerStoreId,
    printerStats,
    loadPrinters,
    openPrinterEditor,
    submitPrinter,
    removePrinter,
  }
}

function parseStoreIds(value: string): string[] {
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed.map(String) : []
  } catch {
    return value.split(',').map((item) => item.trim()).filter(Boolean)
  }
}
